/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import includes from 'lodash.includes';
import {entityId} from '../services/ConnectUtils';
import {
  CTCEntity,
  EntityName,
  EntityOperation,
  EntityStatus,
  GenericObject,
  Link,
  ValidateAttributesType
} from '../types/entities';

/**
 * @normalizeEntity
 *
 * Provides a utility which deletes any UI specific property mutations from a CTCEntity.
 * By convention, UI specific properties are to be post-fixed with "UI" so as to ensure
 * CTC specific attributes and values remain standardized and free of UI specific artifacts
 * such as formatting, etc.
 *
 * ```typescript
 *
 *   // augmentation of user entities with UI specific properties ...
 *   export const constructUsersGridRows = (users: User[]): GridResult[] => {
 *     return users.map((user: User) => ({
 *       ...user,
 *       groupUI          : item?.account?.title,
 *       createdOnUI      : formatDateTime(item?.createdOn),
 *       lastModifiedOnUI : formatDateTime(item?.lastModifiedOn)
 *     })) as GridResult[];
 *   };
 *
 * ```
 *
 * @param  {CTCEntity} entity The CTC entity instance which is to have all UI specific
 *         properties removed.
 * @param  {boolean} clone When true, the provided entity will be cloned before being
 *         normalized and returned. This is useful for when an object is frozen or
 *         should not be normalized directly.
 * @return {CTCEntity} The provided entity with all UI augmented properties removed.
 */
export const normalizeEntity = (entity: GenericObject, clone = false): GenericObject => {
  const instance = clone ? {...entity} : entity;
  Object.keys(instance).forEach(property => {
    const n = property.length;
    if (property.substr(n - 2, n) === 'UI') {
      delete instance[property];
    }
  });
  return instance;
};

export type Status = CTCEntity | string;

const verifyStatus = (value: Status, status: EntityStatus): boolean => {
  return ((value as CTCEntity)?.status || value) === status;
};

/**
 * @isEnabled
 *
 * A utility which provides a check against the entity status type
 * and a check for null.
 *
 * ```typescript
 *
 * isEnabled(asset);
 *
 * isEnabled(asset?.status);
 *
 * ```
 *
 * @param  {CTCEntity} value The CTC entity whose status needs checked
 * or the entities status property.
 *
 * */
export const isEnabled = (value: Status): boolean => {
  return verifyStatus(value, EntityStatus.Enabled);
};

/**
 * @isDisabled
 *
 * A utility which provides a check against the entity status type
 * and a check for null.
 *
 * ```typescript
 *
 * isDisabled(asset);
 *
 * isDisabled(asset?.status);
 *
 * ```
 *
 * @param  {CTCEntity} value The CTC entity whose status needs checked
 * or the entities status property.
 *
 * */
export const isDisabled = (value: Status | string): boolean => {
  return verifyStatus(value, EntityStatus.Disabled);
};

/**
 * @isSuspended
 *
 * A utility which provides a check against the entity status type
 * and a check for null.
 *
 * ```typescript
 *
 * isSuspended(asset);
 *
 * isSuspended(asset?.status);
 *
 * ```
 *
 * @param  {CTCEntity} value The CTC entity whose status needs checked
 * or the entities status property.
 *
 * */
export const isSuspended = (value: Status): boolean => {
  return verifyStatus(value, EntityStatus.Suspended);
};

/**
 * @isDeleted
 *
 * A utility which provides a check against the entity status type
 * and a check for null.
 *
 * ```typescript
 *
 * isDeleted(asset);
 *
 * isDeleted(asset?.status);
 *
 * ```
 *
 * @param  {CTCEntity} value The CTC entity whose status needs checked
 * or the entities status property.
 *
 * */
export const isDeleted = (value: Status): boolean => {
  return verifyStatus(value, EntityStatus.Deleted);
};

/**
 * Validates any attribute in a list of entities and returns the filtered entities
 * which pass the validation against the attribute ...
 *
 * ```typescript
 *
 * import {validateAttributes} from '@ion/core/src/entities';
 *
 * const validatedEntities = validateAttributes(entities, entityName, propName, props);
 *
 * ```
 */
export const validateAttributes = ({
  entities,
  entityName,
  propName,
  props
}: ValidateAttributesType) => {
  const resolvedEntities = [];
  for (const entity of entities) {
    if (entity[entityName]?.[propName] &&
      (!props?.[0] || !includes(props, entity[entityName][propName]))) {
      delete entity[entityName][propName];
    }
    resolvedEntities.push(entity);
  }
  return resolvedEntities;
};

/**
 * Given an array of entities, this will return an array of their ids.
 *
 * If entities is an empty array, null, or undefined, this will return an empty array.
 *
 * ```typescript
 *
 * import {getEntityIds} from '@ion/core/src/entities';
 *
 * const devices = [{id: 123}, {id: 456}, {id: 789}];
 *
 * const ids = getEntityIds(devices); // [123, 456, 789]
 *
 * ```
 */
export const getEntityIds = (entities: CTCEntity[]): number[] => entities?.map(({id}) => id) || [];

/**
 * Provide a utility which resolves the name of entity based on the implementation
 * of the entity as either it's full representation, or it's `Link` representation.
 *
 *  ```typescript
 *  import {resolveName} from '@ion/core/src/entities/EntityUtils';
 *
 *  // full entity representation ...
 *  resolveName({
 *    name: 'Test';
 *  }); // 'Test'
 *
 *  // hateoas entity link representation ...
 *  resolveName({
 *    title: 'Test';
 *  }); // 'Test'
 *  ```
 *
 * @param   entity either a full entity representation, or it's partial HATEOAS
 *          `Link` representation.
 * @returns The resolved name based on either the presence of the `name` attribute
 *          or the `title` attribute.
 */
export const resolveName = <T extends {name?: string, title?: string}>(entity: T): string => (
  'name' in entity ? entity.name : entity.title
);

/**
 * Provides a utility which returns a valid CTC Permission `rel` value based
 * on the provided `EntityName` and `EntityOperation` arguments.
 *
 *  ```typescript
 *  import {resolvePermission} from '@ion/core/src/entities/EntityUtils';
 *.
 *  resolvePermission('asset', 'create'); // 'asset-create'
 *  resolvePermission('ASSET', 'create'); // 'asset-create'
 *  resolvePermission('asset', 'Create'); // 'asset-create'
 *  resolvePermission('asset', Operation.Create); // 'asset-create'
 *  resolvePermission(Entity.asset, 'create'); // 'asset-create'
 *  resolvePermission(Entity.asset, Operation.Create); // 'asset-create'
 *  ```
 *
 * @param   entity An `Entity` enum value or the string literal entity name.
 * @param   operation An `Operation` enum value or the string literal operation.
 * @returns The resolved entity permission based on the provided entity name based
 *          on the `entity`, and `operation` arguments.
 */
export const resolvePermission = (entity: EntityName, operation: EntityOperation): string => (
  entity && operation ? `${entity.toLowerCase()}-${operation.toLowerCase()}` : ''
);

/**
 * Compair entities and return true if found false otherwise
 *
 * ```typescript
 *
 * import {isSameEntities} from '@ion/core/src/entities';
 *
 * const isSameEntities = isSameEntities(entity1, entity2);
 *
 * ```
 */
export const isSameEntities = (...entities: CTCEntity[] | Link[]) => {
  const id = entityId(entities?.[0]);
  return !entities?.some(entity => !(id === entityId(entity)));
};
